const React = require('react');
const { createElement: h, useState, useEffect, useCallback } = React;
const { listWorkflowHttpRequests, loadWorkflowHttpRequest } = require('../lib/workflowStorage');
require('./WorkflowHttpRequests.css');

// Popup log of the HTTP calls made to this workflow's public run endpoint (its
// HTTP trigger). One popup, three views:
//   list    — newest calls first; click a row to see it
//   detail  — that call's payload + response, with "Run again"
//   compose — method + payload, sends a real call to the public run URL
// The run URL is the one the cURL preview shows: the live version, or staging
// while Listen mode is on.
//
// Props:
//   workflowId           string  — the open workflow's id
//   runUrl               string  — public run URL to send to (empty hides Run)
//   startHyperparameters array   — Start node params, used to prefill a payload
//   onClose              () => void

function formatWhen(value) {
  if (!value) return '';
  var when = new Date(value);
  if (isNaN(when.getTime())) return '';
  return when.toLocaleString();
}

function formatDuration(ms) {
  var value = Number(ms) || 0;
  if (value < 1000) return value + 'ms';
  return (value / 1000).toFixed(1) + 's';
}

// Stored bodies are compact text — pretty-print them when they are JSON.
function formatBody(text) {
  var raw = String(text || '');
  if (!raw.trim()) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch (error) {
    return raw;
  }
}

function statusTone(statusCode) {
  var code = Number(statusCode) || 0;
  if (code >= 200 && code < 300) return 'ok';
  if (code >= 400) return 'bad';
  return 'warn';
}

// A starting payload built from the Start node's params, same key/default
// pairs the cURL preview uses.
function buildDefaultPayload(startHyperparameters) {
  var body = {};
  (startHyperparameters || []).forEach(function(param) {
    if (!param || !param.key || !String(param.key).trim()) return;
    body[param.key] = param.default !== undefined && param.default !== '' ? param.default : '';
  });
  return JSON.stringify(body, null, 2);
}

// The run URL can be same-origin relative ("/api/..."). A copied cURL needs
// the full address to work outside the browser.
function toAbsoluteUrl(url) {
  var value = String(url || '');
  if (!value || /^https?:\/\//i.test(value)) return value;
  if (typeof window === 'undefined' || !window.location) return value;
  return window.location.origin + (value.indexOf('/') === 0 ? '' : '/') + value;
}

// Turn a method + payload into the request itself: URL, headers and body. GET
// carries the payload as query params; POST sends it as the body (JSON when it
// parses, else plain text — the endpoint accepts both). Run and Copy cURL both
// go through here, so the copied command is exactly what Run sends.
function buildRequest(runUrl, method, payloadText) {
  var text = String(payloadText || '').trim();
  var parsed = null;
  try {
    parsed = text ? JSON.parse(text) : {};
  } catch (error) {
    parsed = null;
  }

  var url = toAbsoluteUrl(runUrl);

  if (method === 'GET') {
    var params = new URLSearchParams();
    var source = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    Object.keys(source).forEach(function(key) {
      var value = source[key];
      if (value === null || value === undefined) return params.append(key, '');
      params.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
    });
    var query = params.toString();
    if (query) url += (url.indexOf('?') >= 0 ? '&' : '?') + query;
    return { url: url, method: 'GET', headers: null, body: '' };
  }

  return {
    url: url,
    method: 'POST',
    headers: { 'Content-Type': parsed !== null ? 'application/json' : 'text/plain' },
    body: text
  };
}

// Send one real request to the public run URL.
function sendHttpRequest(runUrl, method, payloadText) {
  var target = buildRequest(runUrl, method, payloadText);
  var options = { method: target.method };
  if (target.headers) options.headers = target.headers;
  if (target.body) options.body = target.body;

  var startedAt = Date.now();
  return fetch(target.url, options).then(function(response) {
    return response.text().then(function(body) {
      return {
        statusCode: response.status,
        durationMs: Date.now() - startedAt,
        body: body
      };
    });
  });
}

// The same request as a cURL command — paste it into Postman's
// Import → Raw text to get a ready-made request.
function buildCurlCommand(runUrl, method, payloadText) {
  var target = buildRequest(runUrl, method, payloadText);
  var parts = ['curl -X ' + target.method + ' "' + target.url + '"'];
  if (target.headers) {
    Object.keys(target.headers).forEach(function(name) {
      parts.push('-H "' + name + ': ' + target.headers[name] + '"');
    });
  }
  // Close the quote, add an escaped one, reopen — the shell's way of putting a
  // single quote inside a single-quoted string.
  if (target.body) parts.push('-d \'' + target.body.replace(/'/g, '\'\\\'\'') + '\'');
  return parts.join(' \\\n  ');
}

function copyTextValue(value, onCopied) {
  if (!value) return;

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(value).then(onCopied).catch(function() {});
    return;
  }

  var input = document.createElement('textarea');
  input.value = value;
  document.body.appendChild(input);
  input.select();
  try {
    document.execCommand('copy');
    onCopied();
  } catch (error) {}
  document.body.removeChild(input);
}

function WorkflowHttpRequests(props) {
  var workflowId = props && props.workflowId ? String(props.workflowId) : '';
  var runUrl = props && props.runUrl ? String(props.runUrl) : '';
  var startHyperparameters = props && Array.isArray(props.startHyperparameters) ? props.startHyperparameters : [];
  var onClose = props && typeof props.onClose === 'function' ? props.onClose : function() {};

  var [view, setView] = useState('list');
  var [rows, setRows] = useState([]);
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState('');
  // The logged call shown in the detail view (null while it loads).
  var [selected, setSelected] = useState(null);
  var [method, setMethod] = useState('POST');
  var [payloadText, setPayloadText] = useState('');
  var [sending, setSending] = useState(false);
  var [result, setResult] = useState(null);
  var [curlCopied, setCurlCopied] = useState(false);

  var refresh = useCallback(function() {
    if (!workflowId) return;
    setLoading(true);
    setError('');
    listWorkflowHttpRequests(workflowId)
      .then(function(list) {
        setRows(Array.isArray(list) ? list : []);
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Could not load HTTP requests.');
      })
      .finally(function() {
        setLoading(false);
      });
  }, [workflowId]);

  useEffect(function() { refresh(); }, [refresh]);

  function showList() {
    setView('list');
    setError('');
    setSelected(null);
  }

  function openDetail(row) {
    setView('detail');
    setSelected(null);
    setError('');
    loadWorkflowHttpRequest(workflowId, row.id)
      .then(function(detail) {
        setSelected(detail || null);
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Could not load this request.');
      });
  }

  // Opens the composer, prefilled either from a logged call ("Run again") or
  // from the Start node's params (the top-level Run button).
  function openCompose(source) {
    setMethod(source && source.method === 'GET' ? 'GET' : 'POST');
    setPayloadText(source ? formatBody(source.payload) : buildDefaultPayload(startHyperparameters));
    setResult(null);
    setError('');
    setView('compose');
  }

  function handleSend() {
    if (!runUrl || sending) return;
    setSending(true);
    setError('');
    setResult(null);
    sendHttpRequest(runUrl, method, payloadText)
      .then(function(sent) {
        setResult(sent);
        // The call was logged server-side — pick it up for the list view.
        refresh();
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'The request could not be sent.');
      })
      .finally(function() {
        setSending(false);
      });
  }

  // Copies the request as cURL — from the logged call in the detail view, from
  // the editor in the composer.
  function handleCopyCurl(method, payloadText) {
    copyTextValue(buildCurlCommand(runUrl, method, payloadText), function() {
      setCurlCopied(true);
      setTimeout(function() { setCurlCopied(false); }, 1500);
    });
  }

  function renderCopyCurlButton(forMethod, forPayload) {
    if (!runUrl) return null;
    return h('button', {
      className: 'wf-http-secondary-btn',
      onClick: function() { handleCopyCurl(forMethod, forPayload); },
      title: 'Copy as cURL — paste into Postman → Import → Raw text'
    }, curlCopied ? 'Copied!' : 'Copy cURL');
  }

  function renderBodyField(label, text) {
    var body = formatBody(text);
    return h('div', { className: 'wf-http-field' },
      h('div', { className: 'wf-http-field-label' }, label),
      h('pre', { className: 'wf-http-body' }, body || '(empty)')
    );
  }

  function renderList() {
    return h(React.Fragment, null,
      error ? h('div', { className: 'wf-http-error' }, error) : null,
      !loading && !error && rows.length === 0
        ? h('div', { className: 'wf-http-empty' }, 'No HTTP requests yet. Calls to this workflow\'s public run URL show up here.')
        : null,
      h('div', { className: 'wf-http-list' },
        rows.map(function(row) {
          return h('button', {
            className: 'wf-http-row',
            key: row.id,
            onClick: function() { openDetail(row); }
          },
            h('span', { className: 'wf-http-method' }, row.method),
            h('span', { className: 'wf-http-status wf-http-status-' + statusTone(row.statusCode) }, String(row.statusCode || '—')),
            h('span', { className: 'wf-http-mode' }, row.mode),
            h('span', { className: 'wf-http-when' }, formatWhen(row.createdAt)),
            h('span', { className: 'wf-http-duration' }, formatDuration(row.durationMs))
          );
        })
      )
    );
  }

  function renderDetail() {
    if (error) return h('div', { className: 'wf-http-error' }, error);
    if (!selected) return h('div', { className: 'wf-http-empty' }, 'Loading…');
    return h(React.Fragment, null,
      h('div', { className: 'wf-http-detail-meta' },
        h('span', { className: 'wf-http-method' }, selected.method),
        h('span', { className: 'wf-http-status wf-http-status-' + statusTone(selected.statusCode) }, String(selected.statusCode || '—')),
        h('span', { className: 'wf-http-mode' }, selected.mode),
        h('span', { className: 'wf-http-when' }, formatWhen(selected.createdAt)),
        h('span', { className: 'wf-http-duration' }, formatDuration(selected.durationMs))
      ),
      renderBodyField('Payload', selected.payload),
      renderBodyField('Response', selected.response)
    );
  }

  function renderCompose() {
    return h(React.Fragment, null,
      h('div', { className: 'wf-http-field' },
        h('div', { className: 'wf-http-field-label' }, 'Sends to'),
        h('div', { className: 'wf-http-url' }, runUrl)
      ),
      h('div', { className: 'wf-http-field' },
        h('div', { className: 'wf-http-compose-head' },
          h('div', { className: 'wf-http-field-label' }, 'Payload'),
          h('div', { className: 'wf-http-methods' },
            ['GET', 'POST'].map(function(name) {
              return h('button', {
                key: name,
                className: 'wf-http-method-tab' + (method === name ? ' wf-http-method-tab-active' : ''),
                onClick: function() { setMethod(name); }
              }, name);
            })
          )
        ),
        h('textarea', {
          className: 'wf-http-payload-input',
          value: payloadText,
          spellCheck: false,
          rows: 8,
          placeholder: '{\n  "key": "value"\n}',
          onChange: function(event) { setPayloadText(event.target.value); }
        }),
        h('div', { className: 'wf-http-compose-actions' },
          renderCopyCurlButton(method, payloadText),
          h('button', {
            className: 'wf-http-run-btn',
            onClick: handleSend,
            disabled: sending || !runUrl
          }, sending ? 'Running…' : '▶ Run')
        )
      ),
      error ? h('div', { className: 'wf-http-error' }, error) : null,
      result
        ? h(React.Fragment, null,
            h('div', { className: 'wf-http-detail-meta' },
              h('span', { className: 'wf-http-status wf-http-status-' + statusTone(result.statusCode) }, String(result.statusCode)),
              h('span', { className: 'wf-http-duration' }, formatDuration(result.durationMs))
            ),
            renderBodyField('Response', result.body)
          )
        : null
    );
  }

  var titles = { list: 'HTTP Requests', detail: 'Request details', compose: 'New request' };
  var subtitles = {
    list: 'Every call to this workflow\'s public run URL, newest first.',
    detail: 'What the caller sent, and what this workflow answered.',
    compose: 'Send a call to the public run URL and see the response.'
  };

  return h('div', {
    className: 'wf-http-overlay',
    onMouseDown: onClose
  },
    h('div', {
      className: 'wf-http-modal',
      onMouseDown: function(event) { event.stopPropagation(); }
    },
      h('div', { className: 'wf-http-head' },
        h('div', null,
          h('div', { className: 'wf-http-title' },
            view !== 'list'
              ? h('button', { className: 'wf-http-back', onClick: showList, title: 'Back to the list' }, '←')
              : null,
            titles[view]
          ),
          h('div', { className: 'wf-http-subtitle' }, subtitles[view])
        ),
        h('div', { className: 'wf-http-head-actions' },
          view === 'list' && runUrl
            ? h('button', {
                className: 'wf-http-run-btn',
                onClick: function() { openCompose(null); },
                title: 'Send a request to the public run URL'
              }, '▶ Run')
            : null,
          view === 'detail' && selected
            ? renderCopyCurlButton(selected.method, selected.payload)
            : null,
          view === 'detail' && runUrl && selected
            ? h('button', {
                className: 'wf-http-run-btn',
                onClick: function() { openCompose(selected); },
                title: 'Send this request again'
              }, '▶ Run again')
            : null,
          view === 'list'
            ? h('button', {
                className: 'wf-http-secondary-btn',
                onClick: refresh,
                disabled: loading
              }, loading ? 'Loading…' : 'Refresh')
            : null,
          h('button', { className: 'wf-http-close', onClick: onClose, title: 'Close' }, '×')
        )
      ),
      h('div', { className: 'wf-http-content' },
        view === 'list' ? renderList() : (view === 'detail' ? renderDetail() : renderCompose())
      )
    )
  );
}

module.exports = WorkflowHttpRequests;
